import { useState, type ReactNode } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMoon, faPaperPlane, faXmark } from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";

import { Button } from "@/components/ui/button/Button";
import {
  ConfirmCaseRerunDialog,
  type CaseRerunConfirmOptions,
} from "@/components/case-checking/ConfirmCaseRerunDialog";
import type { ReviewProgressSummary } from "@/components/case-checking/guidedReviewProgress";
import type { DocumentAdditionQueue } from "@/services/api/case-checking";

function formatSchedule(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export type DocumentQueueBatchProps = {
  queue: DocumentAdditionQueue | null;
  caseId?: string;
  sourceReviewRunId?: string | null;
  isBusy?: boolean;
  processingBlocked?: boolean;
  reviewProgress?: ReviewProgressSummary | null;
  onSubmitNow: (options: CaseRerunConfirmOptions) => void;
  onScheduleTonight: (options: CaseRerunConfirmOptions) => void;
  onCancelSchedule: () => void;
  children?: ReactNode;
};

export function DocumentQueueBatch({
  queue,
  caseId,
  sourceReviewRunId,
  isBusy = false,
  processingBlocked = false,
  reviewProgress = null,
  onSubmitNow,
  onScheduleTonight,
  onCancelSchedule,
  children,
}: DocumentQueueBatchProps) {
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [tonightConfirmOpen, setTonightConfirmOpen] = useState(false);
  const items = queue?.items ?? [];
  const isScheduled = queue?.status === "scheduled";
  const hasItems = items.length > 0;
  if (!hasItems && !isScheduled) {
    return null;
  }

  const disabled = isBusy || processingBlocked;
  const scheduleLabel = formatSchedule(queue?.scheduled_submit_at);
  const headerTitle = isScheduled && scheduleLabel ? `Queued for ${scheduleLabel}` : "Queue";

  const handleConfirmSubmit = (options: CaseRerunConfirmOptions) => {
    onSubmitNow(options);
    setSubmitConfirmOpen(false);
  };

  const handleConfirmTonight = (options: CaseRerunConfirmOptions) => {
    onScheduleTonight(options);
    setTonightConfirmOpen(false);
  };

  return (
    <>
      <div
        className={clsx(
          "rounded-lg border border-slate-600/35 bg-slate-800/25 p-3 space-y-3",
          "dark:border-white/10 dark:bg-slate-900/35",
        )}
        data-testid="document-queue-batch"
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{headerTitle}</p>
            {hasItems ? (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Submitting queued files re-runs the case from document triage and may update assessment results.
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 flex-wrap justify-end gap-2" data-testid="document-addition-queue-actions">
            {isScheduled ? (
              <Button
                color="slate"
                type="button"
                disabled={disabled}
                icon={<FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5" />}
                onClick={() => onCancelSchedule()}
                data-testid="document-addition-queue-tonight"
              >
                Cancel tonight
              </Button>
            ) : (
              <Button
                color="slate"
                type="button"
                disabled={disabled || !hasItems}
                icon={<FontAwesomeIcon icon={faMoon} className="h-3.5 w-3.5" />}
                onClick={() => setTonightConfirmOpen(true)}
                data-testid="document-addition-queue-tonight"
              >
                Process tonight
              </Button>
            )}
            <Button
              color="indigo"
              type="button"
              disabled={disabled || !hasItems}
              loading={isBusy}
              icon={<FontAwesomeIcon icon={faPaperPlane} className="h-3.5 w-3.5" />}
              onClick={() => setSubmitConfirmOpen(true)}
              data-testid="document-addition-queue-submit-now"
            >
              Submit now
            </Button>
          </div>
        </div>

        {hasItems ? <div className="space-y-2">{children}</div> : null}
      </div>

      <ConfirmCaseRerunDialog
        open={submitConfirmOpen}
        onClose={() => setSubmitConfirmOpen(false)}
        onConfirm={handleConfirmSubmit}
        action="submit_now"
        progress={reviewProgress}
        caseId={caseId}
        sourceReviewRunId={sourceReviewRunId}
        isLoading={isBusy}
      />

      <ConfirmCaseRerunDialog
        open={tonightConfirmOpen}
        onClose={() => setTonightConfirmOpen(false)}
        onConfirm={handleConfirmTonight}
        action="tonight"
        progress={reviewProgress}
        caseId={caseId}
        sourceReviewRunId={sourceReviewRunId}
        isLoading={isBusy}
      />
    </>
  );
}
